// frontend/src/pages/partner/PurchasePacksPartner.jsx
import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ShoppingCartIcon,
  CheckCircleIcon,
  XCircleIcon,
  XMarkIcon,
  BanknotesIcon,
} from '@heroicons/react/24/solid';
import API from '../../services/api';
import './PurchasePacksPartner.css';

const generateIdempotencyKey = () => {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const userId = user.id || 'anonymous';
  const timestamp = Date.now();
  const random = Math.random().toString(36).substring(2, 15);
  return `${userId}_${timestamp}_${random}`;
};

// ── Métadonnées statiques par tier ──────────────────────────────
const PACK_META = [
  {
    tier: 'Bronze',
    tierLabel: 'Entrepreneur',
    icon: 'fa-seedling',
    glowColor: '#3b82f6',
    borderColor: 'rgba(59,130,246,0.35)',
    badgeBg: 'rgba(59,130,246,0.18)',
    badgeColor: '#024ba0',
    features: [
      'API Access',
      '2 Clés API_KEY',
      'Webhook Notifications',
      'Support Standard',
    ],
    isHighlighted: false,
    isCustom: false,
  },
  {
    tier: 'Argent',
    tierLabel: 'Scale-Up',
    icon: 'fa-chart-line',
    glowColor: '#10b981',
    borderColor: 'rgba(16,185,129,0.35)',
    badgeBg: 'rgba(0, 255, 170, 0.18)',
    badgeColor: '#03a762',
    bonusLabel: '+10% bonus',
    features: [
      'Tout Bronze inclus',
      '5 Clés API_KEY',
      'Atomic Transactions',
      'Priority Support',
    ],
    isHighlighted: false,
    isCustom: false,
  },
  {
    tier: 'Or',
    tierLabel: 'Business',
    icon: 'fa-crown',
    glowColor: '#f59e0b',
    borderColor: 'rgba(245,158,11,0.45)',
    badgeBg:' rgb(241, 152, 0)',
    badgeColor: '#281b01',
    bonusLabel: '+20% bonus',
    features: [
      'Tout Argent inclus',
      'Clés API_KEY illimitées',
      'Full Audit Logs',
      'Dedicated Manager',
    ],
    isHighlighted: true,
    isCustom: false,
  },
  {
    tier: 'Platine',
    tierLabel: 'Entreprise',
    icon: 'fa-infinity',
    glowColor: '#4900f5',
    borderColor: 'rgba(139,92,246,0.35)',
    badgeBg: 'rgba(85, 38, 197, 0.18)',
    badgeColor: '#4828c9',
    features: [
      'Tout Or inclus',
      'Dedicated Server Node',
      'SLA 99.9%',
      'Volume Discount',
    ],
    isHighlighted: false,
    isCustom: true,
  },
];

const PaymentResultModal = ({ result, onClose, onViewWallet }) => {
  const isSuccess = result.type === 'success';
  return (
    <motion.div
      className="prm-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <motion.div
        className="prm-modal"
        initial={{ opacity: 0, scale: 0.86, y: 28 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.92, y: 16 }}
        transition={{ type: 'spring', stiffness: 320, damping: 28 }}
      >
        <button className="prm-close" onClick={onClose}>
          <XMarkIcon style={{ width: 16, height: 16 }} />
        </button>

        <motion.div
          className={`prm-icon-wrap prm-icon-wrap--${isSuccess ? 'success' : 'fail'}`}
          initial={{ scale: 0, rotate: -15 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ type: 'spring', stiffness: 300, damping: 18, delay: 0.1 }}
        >
          {isSuccess
            ? <CheckCircleIcon style={{ width: 52, height: 52 }} />
            : <XCircleIcon style={{ width: 52, height: 52 }} />
          }
        </motion.div>

        <h2 className="prm-title">
          {isSuccess ? 'Paiement accepté !' : 'Paiement annulé'}
        </h2>
        <p className="prm-subtitle">
          {isSuccess
            ? 'Stripe a confirmé votre paiement. Les points seront crédités sur votre wallet momentanément.'
            : "La transaction a été annulée. Aucun montant n'a été débité."}
        </p>

        {isSuccess && (result.pack_name || result.points || result.amount) && (
          <div className="prm-details">
            {result.pack_name && (
              <div className="prm-row">
                <span>Pack</span>
                <strong>{result.pack_name}</strong>
              </div>
            )}
            {result.points && (
              <div className="prm-row">
                <span>Points ajoutés</span>
                <strong className="prm-pts">{Number(result.points).toLocaleString('fr-FR')} pts</strong>
              </div>
            )}
            {result.amount && (
              <div className="prm-row">
                <span>Montant payé</span>
                <strong>{result.amount} TND</strong>
              </div>
            )}
            <div className="prm-row">
              <span>Date</span>
              <strong>{new Date().toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' })}</strong>
            </div>
          </div>
        )}

        <div className="prm-actions">
          {isSuccess ? (
            <>
              <motion.button className="prm-btn prm-btn--primary" onClick={onViewWallet} whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
                <BanknotesIcon style={{ width: 16, height: 16 }} />
                Voir mon wallet
              </motion.button>
              <motion.button className="prm-btn prm-btn--ghost" onClick={onClose} whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
                Continuer les achats
              </motion.button>
            </>
          ) : (
            <motion.button className="prm-btn prm-btn--primary" onClick={onClose} whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
              Réessayer
            </motion.button>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

const cardVariants = {
  hidden: { opacity: 0, y: 32 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { type: 'spring', stiffness: 240, damping: 24, delay: i * 0.09 },
  }),
};

const PurchasePacksPartner = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const preSelectedPackId = queryParams.get('pack');

  const [b2bPacks, setB2bPacks] = useState([]);
  const [wallet, setWallet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState(null);
  const [purchasing, setPurchasing] = useState(false);
  const [paymentModal, setPaymentModal] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [packsRes, walletRes] = await Promise.all([
        API.get('/packs'),
        API.get('/wallet/my-wallet'),
      ]);
      setB2bPacks(packsRes.data.packs || []);
      setWallet(walletRes.data.wallet);
    } catch (err) {
      console.error('Erreur chargement données:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    if (queryParams.get('success') === '1') {
      const raw = localStorage.getItem('pending_payment');
      const data = raw ? JSON.parse(raw) : {};
      setPaymentModal({ type: 'success', ...data });
      if (raw) localStorage.removeItem('pending_payment');
      navigate(location.pathname, { replace: true });
    } else if (queryParams.get('canceled') === '1') {
      setPaymentModal({ type: 'canceled' });
      navigate(location.pathname, { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (preSelectedPackId && b2bPacks.length > 0) {
      const pack = b2bPacks.find(p => p.id === parseInt(preSelectedPackId));
      if (pack) handleStripePurchase(pack);
    }
  }, [preSelectedPackId, b2bPacks]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleStripePurchase = async (pack) => {
    setPurchasing(true);
    const idempotencyKey = generateIdempotencyKey();
    localStorage.setItem('pending_payment', JSON.stringify({
      key: idempotencyKey,
      pack_id: pack.id,
      pack_name: pack.name,
      points: pack.points_amount,
      amount: pack.price_eur,
      timestamp: Date.now(),
    }));
    try {
      const response = await API.post('/stripe/create-checkout-session', {
        pack_id: pack.id,
        points: pack.points_amount,
        amount: pack.price_eur,
        currency: 'eur',
        idempotency_key: idempotencyKey,
        success_url: `${window.location.origin}/dashboard/partner/purchase-packs?success=1`,
        cancel_url:  `${window.location.origin}/dashboard/partner/purchase-packs?canceled=1`,
      });
      if (response.data.url) {
        window.location.href = response.data.url;
      } else {
        setMsg({ type: 'error', text: 'Erreur : pas d\'URL de paiement reçue.' });
        localStorage.removeItem('pending_payment');
        setPurchasing(false);
      }
    } catch (error) {
      console.error('Erreur paiement Stripe:', error);
      setMsg({ type: 'error', text: error.response?.data?.error || 'Erreur lors du paiement.' });
      localStorage.removeItem('pending_payment');
      setPurchasing(false);
    }
  };

  if (loading) {
    return (
      <div className="pp-loading">
        <motion.div
          className="pp-spinner"
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
        />
        <span>Chargement des packs…</span>
      </div>
    );
  }

  // Fusionner données API + métadonnées statiques
  const mergedPacks = PACK_META.map((meta, i) => ({
    meta,
    api: b2bPacks[i] || null,
  }));

  return (
    <>
    <div className="pp-outer">
      {/* ── Hero ────────────────────────────────────────────────── */}
      <div className="pp-hero">
        <div className="pp-hero-orb pp-hero-orb-1" />
        <div className="pp-hero-orb pp-hero-orb-2" />
        <div className="pp-hero-left">
          <div className="pp-hero-icon">
            <ShoppingCartIcon style={{ width: 26, height: 26, color: 'white' }} />
          </div>
          <div>
            <h1>Packs de Points B2B</h1>
            <p>Sélectionnez le volume de points adapté à vos services</p>
          </div>
        </div>
        <div style={{ zIndex: 1 }}>
          <div className="pp-hero-stat-pill">
            <i className="fas fa-wallet" />
            <span>{wallet?.available_points?.toLocaleString('fr-FR') || '0'} pts disponibles</span>
          </div>
        </div>
      </div>

      {/* ── Fond scène (désactivé) ─────────────────────────────── */}
      <div className="pp-scene">
        <div className="pp-grid-overlay" />
        <div className="pp-ambient-1" />
        <div className="pp-ambient-2" />
      </div>

      {/* ── Contenu ───────────────────────────────────────────── */}
      <div className="pp-content">

        {/* Sub-header eyebrow */}
        <motion.div
          className="pp-header"
          initial={{ opacity: 0, y: -18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
        >
          <div className="pp-header-eyebrow">
            <i className="fas fa-lock"></i> Paiements sécurisés · France
          </div>
          <h1 className="pp-title">Monétisation Flexible</h1>
          <p className="pp-subtitle">
            Choisissez le pack le plus adapté à votre volume d'activité
          </p>
        </motion.div>

        {/* Wallet bar */}
        <motion.div
          className="pp-wallet-bar"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.35 }}
        >
          <div className="pp-wallet-left">
            <div className="pp-wallet-icon">
              <i className="fas fa-wallet"></i>
            </div>
            <div>
              <span className="pp-wallet-label">Solde actuel</span>
              <span className="pp-wallet-value">
                {wallet?.available_points?.toLocaleString('fr-FR') || '0'}
                <span className="pp-wallet-unit"> pts</span>
              </span>
            </div>
          </div>
          <motion.button
            className="pp-wallet-link"
            onClick={() => navigate('/dashboard/partner/wallet')}
            whileHover={{ x: 3 }}
            whileTap={{ scale: 0.97 }}
          >
            Voir mon wallet <i className="fas fa-arrow-right"></i>
          </motion.button>
        </motion.div>

        {/* Alert */}
        <AnimatePresence>
          {msg && (
            <motion.div
              className={`pp-alert pp-alert--${msg.type}`}
              initial={{ opacity: 0, y: -8, height: 0 }}
              animate={{ opacity: 1, y: 0, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
            >
              <i className={`fas ${msg.type === 'success' ? 'fa-check-circle' : msg.type === 'info' ? 'fa-info-circle' : 'fa-exclamation-triangle'}`}></i>
              <span>{msg.text}</span>
              <button className="pp-alert-close" onClick={() => setMsg(null)}>
                <i className="fas fa-times"></i>
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Cartes */}
        <div className="pp-cards-grid">
          {mergedPacks.map(({ meta, api }, i) => (
            <motion.div
              key={meta.tier}
              className={`pp-pack-wrapper ${meta.isHighlighted ? 'is-highlighted' : ''}`}
              variants={cardVariants}
              initial="hidden"
              animate="visible"
              custom={i}
            >
              {/* Lueur derrière la carte */}
              <div
                className="pp-pack-glow"
                style={{ background: `radial-gradient(circle, ${meta.glowColor} 0%, transparent 70%)` }}
              />

              {/* Carte verre */}
              <div
                className="pp-pack-card"
                style={meta.isHighlighted ? { borderColor: meta.borderColor } : {}}
              >
                {/* Badge Recommandé */}
                {meta.isHighlighted && (
                  <div className="pp-recommended">
                    <i className="fas fa-star"></i> Recommandé
                  </div>
                )}

                {/* Tier badge */}
                <div
                  className="pp-tier-badge"
                  style={{ background: meta.badgeBg, color: meta.badgeColor }}
                >
                  <i className={`fas ${meta.icon}`}></i>
                  {meta.tier} · {meta.tierLabel}
                </div>

                {/* Prix & Points */}
                {meta.isCustom ? (
                  <div className="pp-price-block">
                    <div className="pp-price-main">Sur Mesure</div>
                    <div className="pp-price-sub">Volume Illimité</div>
                  </div>
                ) : api ? (
                  <div className="pp-price-block">
                    <div className="pp-price-main">
                      {api.price_eur} <span className="pp-price-currency">TND</span>
                    </div>
                    <div className="pp-price-pts">
                      {api.points_amount?.toLocaleString('fr-FR')}
                      <span className="pp-price-pts-unit"> points</span>
                      {meta.bonusLabel && (
                        <span className="pp-bonus-tag">{meta.bonusLabel}</span>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="pp-price-block">
                    <div className="pp-price-main">—</div>
                    <div className="pp-price-sub">Données indisponibles</div>
                  </div>
                )}

                {/* Séparateur */}
                <div className="pp-card-divider" />

                {/* Features */}
                <ul className="pp-features">
                  {meta.features.map((f) => (
                    <li key={f}>
                      <span
                        className="pp-feat-check"
                        style={{ color: meta.glowColor }}
                      >
                        <i className="fas fa-check"></i>
                      </span>
                      {f}
                    </li>
                  ))}
                </ul>

                {/* CTA */}
                {meta.isCustom ? (
                  <motion.button
                    className="pp-cta pp-cta--ghost"
                    onClick={() => navigate('/dashboard/partner/settings')}
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                  >
                    <i className="fas fa-envelope"></i> Contactez-nous
                  </motion.button>
                ) : (
                  <motion.button
                    className={`pp-cta ${meta.isHighlighted ? 'pp-cta--primary' : 'pp-cta--default'}`}
                    onClick={() => api && handleStripePurchase(api)}
                    disabled={purchasing || !api}
                    whileHover={!purchasing && api ? { scale: 1.03 } : {}}
                    whileTap={!purchasing && api ? { scale: 0.97 } : {}}
                  >
                    {purchasing ? (
                      <>
                        <motion.i
                          className="fas fa-spinner"
                          animate={{ rotate: 360 }}
                          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                        />
                        En cours…
                      </>
                    ) : (
                      <>
                        <i className="fas fa-bolt"></i>
                        Acheter avec Stripe
                      </>
                    )}
                  </motion.button>
                )}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Pied de page sécurité */}
        <motion.div
          className="pp-security-footer"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
        >
          <div className="pp-security-item">
            <i className="fas fa-shield-alt"></i>
            <span>Sécurisé par <strong>Runit</strong> (France)</span>
          </div>
          <div className="pp-security-sep" />
          <div className="pp-security-item">
            <i className="fab fa-stripe"></i>
            <span>Paiements traités par <strong>Stripe</strong></span>
          </div>
          <div className="pp-security-sep" />
          <div className="pp-security-item">
            <i className="fas fa-file-contract"></i>
            <span>Transactions réelles · TVA France applicable</span>
          </div>
        </motion.div>
      </div>
    </div>

    <AnimatePresence>
      {paymentModal && (
        <PaymentResultModal
          result={paymentModal}
          onClose={() => { setPaymentModal(null); fetchData(); }}
          onViewWallet={() => navigate('/dashboard/partner/wallet')}
        />
      )}
    </AnimatePresence>
    </>
  );
};

export default PurchasePacksPartner;
